import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUser,
    faEnvelope,
    faChartBar,
    faClipboardCheck,
    faExclamationTriangle,
    faBullhorn,
    faCheckCircle,
    faCalendarAlt,
    faStar,
    faSpinner,
    faCircleCheck,
    faCircleXmark,
    faCircleExclamation,
    faChevronUp,
    faChevronDown,
    faLock,
    faUnlock,
    faPhone,
    faFileContract
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';

const CatiInterviewerDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [showSurveysSection, setShowSurveysSection] = useState(false);
    const [showFormationsSection, setShowFormationsSection] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const [unlocking, setUnlocking] = useState(false);
    const [showUnlockModal, setShowUnlockModal] = useState(false);
    const [confidentialData, setConfidentialData] = useState(null);
    const [confidentialLoading, setConfidentialLoading] = useState(false);

    useEffect(() => {
        fetchInterviewerDetail();
        fetchConfidentialData();
    }, [id]);

    const fetchConfidentialData = async () => {
        try {
            setConfidentialLoading(true);
            const response = await api.get(`/confidential-data/interviewer/${id}?masked=false`);
            if (response.data.success) {
                setConfidentialData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching confidential data:', error);
            setConfidentialData(null);
        } finally {
            setConfidentialLoading(false);
        }
    };

    const fetchInterviewerDetail = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get(`/cati/interviewers/${id}`);
            
            if (response.data.success) {
                setData(response.data.data);
                setIsLocked(response.data.data?.interviewer?.is_locked || false);
            } else {
                setError(response.data.message || 'Erreur lors du chargement des détails');
            }
        } catch (error) {
            console.error('Error fetching interviewer detail:', error);
            setError(error.response?.data?.message || 'Erreur lors du chargement des détails');
        } finally {
            setLoading(false);
        }
    };

    const handleUnlock = async () => {
        try {
            setUnlocking(true);
            setError('');
            
            await api.post(`/interviewers/${id}/unlock-account`);
            setIsLocked(false);
            setShowUnlockModal(false);
            fetchInterviewerDetail(); // Refresh interviewer data
        } catch (error) {
            console.error('Error unlocking account:', error);
            if (error.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('Erreur lors du déverrouillage du compte');
            }
        } finally {
            setUnlocking(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active':
                return <FontAwesomeIcon icon={faCircleCheck} className="text-green-500" />;
            case 'suspended':
                return <FontAwesomeIcon icon={faCircleXmark} className="text-red-500" />;
            case 'inactive':
                return <FontAwesomeIcon icon={faCircleExclamation} className="text-yellow-500" />;
            default:
                return <FontAwesomeIcon icon={faCircleXmark} className="text-gray-400" />;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active':
                return 'Actif';
            case 'suspended':
                return 'Suspendu';
            case 'inactive':
                return 'Inactif';
            default:
                return 'Inconnu';
        }
    };

    const getVerifiedIcon = (verified) => {
        switch (verified) {
            case 'yes':
                return <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />;
            case 'no':
                return <FontAwesomeIcon icon={faCircleXmark} className="text-red-500" />;
            case 'partially':
                return <FontAwesomeIcon icon={faCircleExclamation} className="text-yellow-500" />;
            default:
                return <FontAwesomeIcon icon={faCircleXmark} className="text-gray-400" />;
        }
    };

    const getVerifiedText = (verified) => {
        switch (verified) {
            case 'yes':
                return 'Vérifié';
            case 'no':
                return 'Non vérifié';
            case 'partially':
                return 'Partiellement';
            default:
                return 'Non vérifié';
        }
    };

    const renderStarRating = (rating, rawRating, totalChecks) => {
        let numRating;
        if (rawRating !== null && rawRating !== undefined && !isNaN(rawRating) && Number(rawRating) > 0) {
            numRating = Number(rawRating);
        } else if (rating !== null && rating !== undefined && !isNaN(rating)) {
            numRating = (Number(rating) / 100) * 5;
        } else {
            return (
                <div className="flex items-center gap-1">
                    <span className="text-gray-400 text-sm">N/A</span>
                </div>
            );
        }

        numRating = Math.max(0, Math.min(5, numRating));
        
        const stars = [];
        for (let i = 0; i < 5; i++) {
            const starValue = numRating - i;
            let fillPercentage = 0;
            
            if (starValue >= 1) {
                fillPercentage = 100;
            } else if (starValue > 0) {
                fillPercentage = starValue * 100;
            }
            
            stars.push(
                <span 
                    key={i} 
                    className="relative inline-block" 
                    style={{ width: '14px', height: '14px', display: 'inline-block', verticalAlign: 'middle' }}
                >
                    <FontAwesomeIcon 
                        icon={faStar} 
                        className="absolute inset-0 text-gray-300"
                        style={{ fontSize: '14px' }}
                    />
                    <FontAwesomeIcon 
                        icon={faStar} 
                        className="absolute inset-0 text-yellow-400"
                        style={{ 
                            fontSize: '14px',
                            clipPath: `inset(${100 - fillPercentage}% 0 0 0)`,
                            WebkitClipPath: `inset(${100 - fillPercentage}% 0 0 0)`
                        }}
                    />
                </span>
            );
        }
        
        return (
            <div className="flex items-center gap-1">
                {stars}
            </div>
        );
    };

    const renderCircularGauge = (value, color, label, maxValue = 100) => {
        const percentage = value !== null && value !== undefined ? Math.min(Math.max(value, 0), maxValue) : 0;
        const radius = 54;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference * (1 - percentage / maxValue);

        return (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center">
                <div className="text-sm text-gray-600 mb-4">{label}</div>
                <div className="relative w-40 h-40">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                        <circle
                            cx="60"
                            cy="60"
                            r={radius}
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="12"
                        />
                        <circle
                            cx="60"
                            cy="60"
                            r={radius}
                            fill="none"
                            stroke={color}
                            strokeWidth="12"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={offset}
                            style={{
                                transition: 'stroke-dashoffset 1.5s ease-in-out',
                            }}
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-4xl font-bold" style={{ color: color }}>
                            {value !== null && value !== undefined ? Math.round(value) : '-'}
                        </span>
                        <span className="text-sm text-gray-500">/ {maxValue}</span>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="text-center relative z-10 py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement des détails...</p>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-gray-50 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="relative z-10 p-6">
                    <div className="bg-white rounded-xl p-6 shadow-lg border border-red-200">
                        <div className="flex items-center text-red-600 mb-4">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                            <span>{error || 'Erreur lors du chargement'}</span>
                        </div>
                        <button
                            onClick={() => navigate('/cati/interviewers')}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            Retour à la liste
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const { interviewer, surveys, penalties, scores, control_ratings, completed_formations } = data;

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>

            <div className="relative z-10 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-6 flex justify-between items-start">
                        <div>
                        <h1 className="text-3xl font-bold text-gray-900">{interviewer.name}</h1>
                        <p className="text-gray-600 mt-1">Détails de l'enquêteur CATI</p>
                        </div>
                        {currentUser?.role === 'admin_it' && (
                            <button
                                onClick={handleUnlock}
                                disabled={!isLocked || unlocking}
                                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                                    isLocked && !unlocking
                                        ? 'bg-orange-600 text-white hover:bg-orange-700'
                                        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                }`}
                                title={isLocked ? 'Déverrouiller le compte' : 'Le compte n\'est pas verrouillé'}
                            >
                                {unlocking ? (
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                ) : (
                                    <FontAwesomeIcon icon={isLocked ? faUnlock : faLock} />
                                )}
                                <span>
                                    {unlocking ? 'Déverrouillage...' : isLocked ? 'Déverrouiller' : 'Déverrouillé'}
                                </span>
                            </button>
                        )}
                    </div>

                    {/* Interviewer Info and Poste Cards */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {/* Informations Personnelles Card */}
                        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                <FontAwesomeIcon icon={faUser} className="mr-2 text-red-600" />
                                Informations Personnelles
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="p-4 bg-gray-50 rounded-lg min-w-0">
                                    <p className="text-xs text-gray-500 mb-1">Email</p>
                                    <p className="text-sm font-medium text-gray-900 flex items-center min-w-0">
                                        <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-gray-400 flex-shrink-0" />
                                        <span className="break-words min-w-0">{interviewer.email}</span>
                                    </p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg min-w-0">
                                    <p className="text-xs text-gray-500 mb-1">Téléphone</p>
                                    {confidentialLoading ? (
                                        <p className="text-sm font-medium text-gray-500 flex items-center">
                                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                            Chargement...
                                        </p>
                                    ) : (
                                        <p className="text-sm font-medium text-gray-900 flex items-center min-w-0">
                                            <FontAwesomeIcon icon={faPhone} className="mr-2 text-gray-400 flex-shrink-0" />
                                            <span className="break-words min-w-0">
                                                {confidentialData?.phone || <span className="text-gray-400 italic">Non renseigné</span>}
                                            </span>
                                        </p>
                                    )}
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <p className="text-xs text-gray-500 mb-1">Référence</p>
                                    <p className="text-sm font-medium text-gray-900">{interviewer.ref || 'N/A'}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <p className="text-xs text-gray-500 mb-1">Statut</p>
                                    <div className="flex items-center">
                                        {getStatusIcon(interviewer.status)}
                                        <span className="ml-2 text-sm font-medium text-gray-900">
                                            {getStatusText(interviewer.status)}
                                        </span>
                                    </div>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <p className="text-xs text-gray-500 mb-1">Vérification</p>
                                    <div className="flex items-center">
                                        {getVerifiedIcon(interviewer.verified)}
                                        <span className="ml-2 text-sm font-medium text-gray-900">
                                            {getVerifiedText(interviewer.verified)}
                                        </span>
                                    </div>
                                </div>
                                {interviewer.supervisor && (
                                    <div className="p-4 bg-gray-50 rounded-lg">
                                        <p className="text-xs text-gray-500 mb-1">Responsable</p>
                                        <p className="text-sm font-medium text-gray-900">{interviewer.supervisor.name}</p>
                                    </div>
                                )}
                                {interviewer.last_login_at && (
                                    <div className="p-4 bg-gray-50 rounded-lg">
                                        <p className="text-xs text-gray-500 mb-1">Dernière connexion</p>
                                        <p className="text-sm font-medium text-gray-900 flex items-center">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                                            {new Date(interviewer.last_login_at).toLocaleDateString('fr-FR', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                )}
                                {/* Contract Information */}
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <p className="text-xs text-gray-500 mb-1">Contrat</p>
                                    {(interviewer.contrat_signed === true || interviewer.contrat_signed === 1) ? (
                                        <div>
                                            <p className="text-sm font-medium text-green-600 flex items-center mb-1">
                                                <FontAwesomeIcon icon={faFileContract} className="mr-2" />
                                                Signé
                                            </p>
                                            {interviewer.contract_start_date && (
                                                <p className="text-xs text-gray-600">
                                                    Début: {new Date(interviewer.contract_start_date).toLocaleDateString('fr-FR')}
                                                </p>
                                            )}
                                            {interviewer.contract_end_date && (
                                                <p className="text-xs text-gray-600">
                                                    Fin: {new Date(interviewer.contract_end_date).toLocaleDateString('fr-FR')}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-sm font-medium text-gray-400">Non signé</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Poste Card */}
                        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Poste</h3>
                            
                            {(() => {
                                const postToShow = interviewer.current_post || interviewer.dedicated_post;
                                const isDedicated = interviewer.dedicated_post && (!interviewer.current_post || interviewer.current_post.id === interviewer.dedicated_post.id);
                                const isLargeNumber = postToShow && postToShow.number && postToShow.number.toString().length > 8;
                                
                                if (postToShow) {
                                    return (
                                        <div className="text-center py-4">
                                            <div className="mb-4">
                                                <div className="text-sm text-gray-600 mb-2">
                                                    {isDedicated ? 'Poste Dédié' : 'Poste Actuel'}
                                                </div>
                                                {isLargeNumber ? (
                                                    <div className="text-2xl font-bold text-red-600 break-all">
                                                        #{postToShow.number}
                                                    </div>
                                                ) : (
                                                    <div className="text-5xl font-bold text-red-600">
                                                        #{postToShow.number}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {(() => {
                                                // Show date for current_post (assignment start) or dedicated_post (creation date)
                                                const dateToShow = interviewer.current_post?.starts_at || interviewer.dedicated_post?.created_at;
                                                if (dateToShow) {
                                                    return (
                                                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                            <div className="text-sm text-gray-600 mb-1">Depuis le</div>
                                                            <div className="font-semibold text-gray-900">
                                                                {new Date(dateToShow).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: 'numeric'
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div className="text-center py-4">
                                            <FontAwesomeIcon icon={faPhone} className="text-3xl text-gray-300 mb-2" />
                                            <p className="text-sm text-gray-500">Aucun poste actuellement assigné</p>
                                            <p className="text-xs text-gray-400 mt-1">Contactez votre responsable pour obtenir un nouveau poste</p>
                                        </div>
                                    );
                                }
                            })()}
                        </div>
                    </div>

                    {/* Surveys Card - Collapsible */}
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
                        <button
                            onClick={() => setShowSurveysSection(!showSurveysSection)}
                            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                        >
                            <h2 className="text-xl font-bold text-gray-900 flex items-center">
                                <FontAwesomeIcon icon={faChartBar} className="mr-2 text-red-600" />
                                Enquêtes Complétées
                            </h2>
                            <FontAwesomeIcon 
                                icon={showSurveysSection ? faChevronUp : faChevronDown} 
                                className="text-gray-500 transition-transform duration-200"
                            />
                        </button>
                        
                        {showSurveysSection && (
                            <div className="px-6 pb-6 border-t border-gray-100">
                                <div className="mt-4 mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-700">Total des enquêtes complétées (et payées)</span>
                                        <span className="text-2xl font-bold text-red-600">{surveys.total_completed}</span>
                                    </div>
                                </div>
                                {surveys.by_survey.length > 0 ? (
                                    <div className="space-y-2">
                                        {surveys.by_survey.map((survey, index) => (
                                            <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {survey.survey_name} ({survey.limesurvey_id})
                                                        </p>
                                                    </div>
                                                    <div className="ml-4">
                                                        <span className="text-lg font-bold text-red-600">{survey.completed_count}</span>
                                                        <span className="text-sm text-gray-500 ml-1">complété(s)</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <FontAwesomeIcon icon={faChartBar} className="text-4xl text-gray-300 mb-2" />
                                        <p>Aucune enquête complétée</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Penalties Card */}
                    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 mb-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-red-600" />
                            Pénalités
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">Nombre total de pénalités</span>
                                    <span className="text-2xl font-bold text-red-600">{penalties.count || 0}</span>
                                </div>
                            </div>
                            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">Total des points de pénalité</span>
                                    <span className="text-2xl font-bold text-red-600">
                                        {penalties.total_amount ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'MAD' }).format(penalties.total_amount) : '0,00 MAD'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Scores Section */}
                    {scores && (scores.accuracy_rating !== null || scores.prod_component !== null || scores.qual_component !== null) && (
                        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 mb-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900">Scores de Performance</h2>
                                {scores.score_updated_at && (
                                    <span className="text-xs text-gray-500">
                                        Mis à jour: {new Date(scores.score_updated_at).toLocaleDateString('fr-FR')}
                                    </span>
                                )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Overall Score */}
                                {scores.accuracy_rating !== null && renderCircularGauge(
                                    scores.accuracy_rating,
                                    scores.accuracy_rating >= 80 ? '#16a34a' : scores.accuracy_rating >= 60 ? '#ca8a04' : '#dc2626',
                                    'Score Global'
                                )}
                                
                                {/* Productivity Component */}
                                {scores.prod_component !== null && renderCircularGauge(
                                    scores.prod_component,
                                    '#2563eb',
                                    'Productivité'
                                )}
                                
                                {/* Quality Component */}
                                {scores.qual_component !== null && renderCircularGauge(
                                    scores.qual_component,
                                    '#16a34a',
                                    'Qualité'
                                )}
                            </div>

                            {/* Controllers Rating */}
                            {scores.controllers_rating !== null && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-700">Note des Contrôleurs</span>
                                        <span className="text-2xl font-bold text-gray-900">{scores.controllers_rating}/100</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Control Ratings Section */}
                    {control_ratings && (control_ratings.global?.average_rating !== null || (control_ratings.per_survey && control_ratings.per_survey.length > 0)) && (
                        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 mb-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Note de Contrôle</h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Global Rating */}
                                {control_ratings.global?.average_rating !== null && (
                                    <div className="bg-white rounded-lg p-4 sm:p-8 shadow-md border-2 border-red-600">
                                        <div className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Note Globale</div>
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                                            <div className="flex-1 min-w-0">
                                                <div className="scale-110 sm:scale-125 origin-left mb-2">
                                                    {renderStarRating(
                                                        control_ratings.global.average_rating_percentage,
                                                        control_ratings.global.average_rating,
                                                        control_ratings.global.total_checks
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-left sm:text-right flex-shrink-0">
                                                <div className="text-xl sm:text-2xl font-bold text-gray-900">
                                                    {control_ratings.global.average_rating.toFixed(1)}/5
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Per Survey Ratings */}
                                {control_ratings.per_survey && control_ratings.per_survey.length > 0 && (
                                    <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200">
                                        <div className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Par Enquête</div>
                                        <div className="space-y-3 max-h-64 overflow-y-auto">
                                            {control_ratings.per_survey.map((survey) => (
                                                <div key={survey.survey_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 pb-3 border-b border-gray-100 last:border-0">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs sm:text-sm font-medium text-gray-900 truncate break-words">
                                                            {survey.survey_title || `Enquête ID: ${survey.survey_id}`}
                                                            {survey.limesurvey_id && (
                                                                <span className="text-gray-500 ml-1">({survey.limesurvey_id})</span>
                                                            )}
                                                        </div>
                                                        <div className="mt-1">
                                                            {renderStarRating(survey.average_rating_percentage, survey.average_rating, survey.total_checks)}
                                                        </div>
                                                    </div>
                                                    <div className="text-left sm:text-right sm:ml-4 flex-shrink-0">
                                                        <div className="text-base sm:text-lg font-bold text-gray-900">
                                                            {survey.average_rating.toFixed(1)}/5
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Completed Formations Card - Collapsible */}
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200">
                        <button
                            onClick={() => setShowFormationsSection(!showFormationsSection)}
                            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                        >
                            <h2 className="text-xl font-bold text-gray-900 flex items-center">
                                <FontAwesomeIcon icon={faBullhorn} className="mr-2 text-red-600" />
                                Formations Complétées
                            </h2>
                            <FontAwesomeIcon 
                                icon={showFormationsSection ? faChevronUp : faChevronDown} 
                                className="text-gray-500 transition-transform duration-200"
                            />
                        </button>
                        
                        {showFormationsSection && (
                            <div className="px-6 pb-6 border-t border-gray-100">
                                <div className="mt-4">
                                    {completed_formations && completed_formations.length > 0 ? (
                                        <div className="space-y-3">
                                            {completed_formations.map((formation) => (
                                                <div key={formation.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <h3 className="text-base font-semibold text-gray-900 mb-1">
                                                                {formation.title}
                                                            </h3>
                                                            {formation.description && (
                                                                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                                                    {formation.description}
                                                                </p>
                                                            )}
                                                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                                                <div className="flex items-center">
                                                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                                                                    <span>
                                                                        Complétée le {new Date(formation.completed_at).toLocaleDateString('fr-FR', { 
                                                                            year: 'numeric', 
                                                                            month: 'long', 
                                                                            day: 'numeric' 
                                                                        })}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center">
                                                                    <FontAwesomeIcon 
                                                                        icon={formation.completed_method === 'online' ? faCheckCircle : faClipboardCheck} 
                                                                        className={`mr-1 ${formation.completed_method === 'online' ? 'text-green-500' : 'text-blue-500'}`}
                                                                    />
                                                                    <span className="capitalize">
                                                                        {formation.completed_method === 'online' ? 'En ligne' : 'Présence marquée'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <FontAwesomeIcon icon={faBullhorn} className="text-4xl text-gray-300 mb-2" />
                                            <p>Aucune formation complétée</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            <ConfirmationModal
                isOpen={showUnlockModal}
                onClose={() => setShowUnlockModal(false)}
                onConfirm={handleUnlock}
                title="Déverrouiller le compte"
                message="Êtes-vous sûr de vouloir déverrouiller ce compte ? Les tentatives de connexion échouées seront réinitialisées."
                confirmText="Déverrouiller"
                cancelText="Annuler"
                confirmColor="bg-orange-600 hover:bg-orange-700"
                loading={unlocking}
            />
        </div>
    );
};

export default CatiInterviewerDetail;

